import {
    createSlice,
    createAction,
    createAsyncThunk,
    createListenerMiddleware,
    TypedStartListening,
    createSelector
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { ApplicationState, AppDispatch, resetApp } from "./store";
import { LICENSE_ID_STORAGE_KEY } from "../config/constants";
import { isAuthenticated } from "./authentication";
import { generateInitialState } from "utils/common";

interface Entity {
    license?: string;
    oldLicense?: string;
}

const sliceName = "license";

// Middlewares
export const licenseListener = createListenerMiddleware();
export const startLicenseListener = licenseListener.startListening as TypedStartListening<ApplicationState, AppDispatch>;

// Actions
export const initLicense = createAction(
    `${sliceName}/INIT`,
    () => {
        const licenseId = localStorage.getItem(LICENSE_ID_STORAGE_KEY);
        return {
            payload: licenseId,
        };
    }
);

export const setLicense = createAction(
    `${sliceName}/SET_LICENSE`,
    (licenseId: string) => {
        return {
            payload: licenseId,
        };
    }
);

// Slice
export const initialState = generateInitialState<Entity>();

const licenseSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addMatcher(initLicense.match, (state, { payload }) => {
            state.isInitialized = true;

            if (!state.entity && payload) {
                state.entity = {
                    license: payload
                }
            } else if (payload) {
                state.entity = {
                    ...state.entity,
                    license: payload
                }
            }
        });


        builder.addMatcher(setLicense.match, (state, { payload }) => {
            state.isInitialized = true;

            if (!state.entity && payload) {
                state.entity = {
                    license: payload
                }
            } else if (state.entity?.license && payload) {
                state.entity.oldLicense = state.entity.license;
                state.entity.license = payload;
            } else if (payload) {
                state.entity = {
                    ...state.entity,
                    license: payload
                }
            }
        });

        builder.addMatcher(resetApp.match, () => {
            return initialState;
        });
    },
});

// Listeners
startLicenseListener({
    matcher: setLicense.match,
    effect: async (action, api) => {
        localStorage.setItem(LICENSE_ID_STORAGE_KEY, action.payload);
    }
});


// selectors
export const licenseSelector = (s: ApplicationState) => s.license;
export const needsNewLicenseSelector = createSelector(
    [isAuthenticated, licenseSelector],
    (authenticated, license) => {
        return Boolean(authenticated && !license.entity)
    }
);

export default licenseSlice;
